<template>
  <div
    v-if="basketStore.currentItem"
    class="text-center sm:border-l border-b flex relative sm:px-4 sm:flex-col justify-center gap-1"
  >
    <div v-if="hasPricing" class="text-center capitalize tracking-widest text-sm flex flex-col justify-around">
      <!--// removed this to manually show price: && item.price > 0-->
      <span class="flex flex-col items-center px-4 sm:p-0">
        <Toggle
          v-if="authStore.isPortal && !authStore.isBrandedModeUser && !authStore.isExtruderOrExpress"
          class="mb-1"
          label="Show Financials"
          :active="isCostsHidden"
          :inverse="true"
          @toggle="isCostsHidden = $event"
        />
        <div v-show="!isCostsHidden || authStore.isBusiness || authStore.isBrandedModeUser">
          <span v-if="authStore.isBusiness || authStore.isExtruderOrExpress" class="hidden xs:inline"
            >Item Price:
          </span>
          <span v-else class="hidden xs:inline">Item Cost: </span>
          <strong>{{ formatted_item_price }}</strong>
          <span class="text-xs xs:text-sm block xs:inline relative">
            <template v-if="showTax"> inc</template><template v-else> exc</template> vat
            <PriceBreakdownButton />
          </span>
        </div>
      </span>
    </div>
    <div v-if="!hasPricing && !authStore.isBrandedModeUser" class="flex flex-col justify-around text-xs px-2">
      <router-link to="/setup/prices">Click here to setup pricing</router-link>
    </div>
    <div
      v-if="
        sandboxStore.sandboxIsActive ||
        (authStore.superuser && !authStore.isBrandedModeUser) ||
        authStore.isExtruderOrExpressCustomer
      "
      class="text-center hidden xs:flex gap-2 justify-around p-2 pr-4 sm:p-0"
    >
      <button class="btn btn-action md:py-3 md:px-10 whitespace-nowrap" @click="sandboxReport">
        Financial Summary
      </button>
      <button
        class="btn btn-action md:py-3 md:px-10 whitespace-nowrap"
        @click="addToBasket"
        v-text="`Save To ${basketStore.getContractIdentifier?.contractType || 'Basket'}`"
      />
    </div>
    <div v-else class="text-center hidden xs:flex justify-around pr-4 flex-col sm:pr-0">
      <button
        class="btn btn-action btn-lg whitespace-nowrap"
        @click="addToBasket"
        v-text="`Save To ${basketStore.getContractIdentifier?.contractType || 'Basket'}`"
      />
    </div>
  </div>
</template>

<script>
import PriceBreakdownButton from '@/components/shared/nav/PriceBreakdownButton.vue'
import Toggle from '@/components/shared/ui/Toggle.vue'
import { SalesSectorType } from '@/enums'
import { useAuthStore } from '@/pinia/auth'
import { useBasketStore } from '@/pinia/basket'
import { useBrandedStore } from '@/pinia/branded'
import { useCustomerStore } from '@/pinia/customer'
import { useDesignerStore } from '@/pinia/designer'
import { useSandboxStore } from '@/pinia/sandbox'
import alertbox from '@/utils/alertbox'
import { formatCurrency } from '@/utils/currency'
import { mapStores } from 'pinia'

export default {
  components: {
    PriceBreakdownButton,
    Toggle
  },
  props: ['on_designer_page'],
  setup() {
    return { formatCurrency }
  },
  data() {
    return {
      isCostsHidden: true
    }
  },
  computed: {
    ...mapStores(useAuthStore, useBrandedStore, useBasketStore, useSandboxStore, useDesignerStore, useCustomerStore),
    tableOfDefaults() {
      const prefix = '<ul class="pt-3">'
      const suffix = '</ul>'

      const rows = this.basketStore.currentItem.specification
        .filter((spec) => spec.mustBeSelected && !spec.hasBeenSelected)
        .map((spec) => `<li>${spec.name}</li>`)
        .join('')

      return `${prefix}${rows}${suffix}`
    },
    requiresInput() {
      return this.basketStore.currentItem.specification.some((spec) => spec.mustBeSelected && !spec.hasBeenSelected)
    },
    requiresMeasurements() {
      const needsDimensionInput = this.basketStore.currentItem.dimensions.some(
        (dimension) => dimension.requiresInput && !dimension.hasBeenInput
      )

      return this.authStore.isBrandedModeUser
        ? this.basketStore.contractHasSizing && needsDimensionInput
        : needsDimensionInput
    },
    hasPricing() {
      return !this.authStore.isBrandedModeUser || this.customerStore.hasBrandedPricingOn
    },
    basketLink() {
      if (this.authStore.isBusiness) {
        return `/customer/${this.basketStore.customerId}/sales-dashboard/basket`
      }

      return this.authStore.isBrandedModeUser ? this.brandedStore.basketURL : '/basket'
    },
    formatted_item_price() {
      return this.formatCurrency(this.basketStore.getCurrency, this.item_price)
    },
    item_price() {
      return (this.basketStore.currentItem.price + this.getTax()) / this.basketStore.currentItem.quantity
    },
    showTax() {
      return this.authStore.isBrandedModeUser || this.basketStore.salesSectorType === SalesSectorType.RETAIL
    }
  },
  created() {
    if (this.authStore.isExtruderOrExpress) {
      this.isCostsHidden = false
    }
  },
  mounted() {
    if (this.$route.query.isInvalidSpec) {
      this.isInvalidSpec()
      this.$router.replace({ query: null })
    }
  },
  methods: {
    getTax() {
      if (this.showTax) {
        let tax = this.basketStore.currentItem.tax
        for (let i = 0; i < this.basketStore.currentItem.extras?.length; i++) {
          if (this.basketStore.currentItem.extras[i].priceIncTax && this.basketStore.currentItem.extras[i].price) {
            tax += this.basketStore.currentItem.extras[i].priceIncTax - this.basketStore.currentItem.extras[i].price
          }
        }
        return tax
      } else {
        return 0
      }
    },
    async sandboxReport() {
      const url = await this.basketStore.getSandboxReportUrlForCurrentItem()
      const new_window = window.open(url, '_blank')
      if (new_window) {
        new_window.focus()
      }
    },
    async addToBasket() {
      this.dimensionBoxesHighlight(false)

      if (this.authStore.isBrandedModeUser) {
        this.$router.push(this.basketLink)
        return
      }

      if (!this.requiresMeasurements && !this.requiresInput) {
        this.$router.push(this.basketLink)
        return
      }

      if (this.requiresInput) {
        this.isInvalidSpec()
        this.designerStore.SET_OPEN_MENU_TYPE('specification')
        return
      }

      if (this.requiresMeasurements) {
        await alertbox({ title: 'You must supply dimensions before saving' })
        this.designerStore.SET_OPEN_MENU_TYPE('sizes')
        return
      }
    },
    async isInvalidSpec() {
      const { isConfirmed } = await alertbox({
        title: 'You have not made required selections',
        html: `<p>Please review and answer the following questions</p> ${this.tableOfDefaults}`,
        buttons: [{ text: 'Back', style: 'btn-secondary', action: { isConfirmed: false } }]
      })
      if (isConfirmed) {
        await this.basketStore.processingForceDefaults()
        this.$router.push(this.basketLink)
      }
    },
    dimensionBoxesHighlight(highlight) {
      const inputBoxes = document.querySelectorAll('.input-wrapper')
      inputBoxes.forEach((inputBox) => {
        if (highlight) {
          inputBox.classList.add('highlighted')
        } else {
          inputBox.classList.remove('highlighted')
        }
      })
    }
  }
}
</script>
